import { normalize } from './text'
import { activeItemClasses, itemName } from './worldFormat'
import { ITEM_SECTION_SLUG } from './prettyRoutes'
import type { CharacterClass } from '../types/progression'
import type { Item, WorldData } from '../types/world'

export type ClassItem = { id: string; name: string; item: Item; section: number }

const cache = new WeakMap<WorldData, Map<string, { ids: string[]; items: ClassItem[] }>>()

/**
 * IDs de 04_classes que corresponden a la clase y sus evoluciones (por nombre exacto del juego).
 * Solo se usan clases activas; las inactivas no se presentan.
 */
export function catalogClassIds(w: WorldData, cls: CharacterClass): string[] {
  const names = new Set([cls.base, cls.second, cls.third].filter(Boolean).map((n) => normalize(n as string)))
  const ids: string[] = []
  w.classNames.forEach((name, id) => {
    if (names.has(normalize(name)) && (w.activeClassIds.size === 0 || w.activeClassIds.has(id))) ids.push(id)
  })
  return ids
}

const baseItem = (w: WorldData, id: string) => w.items.get(w.variants.get(id)?.itemId ?? id)

/** Clase → equipo compatible según 05_item_classes (solo ítems publicados). */
export function classItems(w: WorldData, cls: CharacterClass): { ids: string[]; items: ClassItem[] } {
  let byClass = cache.get(w)
  if (!byClass) {
    byClass = new Map()
    cache.set(w, byClass)
  }
  const hit = byClass.get(cls.id)
  if (hit) return hit
  const ids = catalogClassIds(w, cls)
  const set = new Set(ids)
  const items: ClassItem[] = []
  if (set.size) {
    w.itemClasses.forEach((_, itemId) => {
      const item = baseItem(w, itemId)
      if (!item) return
      if (!activeItemClasses(w, itemId).some((c) => set.has(c))) return
      items.push({ id: itemId, name: itemName(w, itemId), item, section: Number(itemId.split('@')[0].split(',')[0]) })
    })
  }
  items.sort((a, b) => a.section - b.section || (a.item.requiredLevel ?? 0) - (b.item.requiredLevel ?? 0) || a.name.localeCompare(b.name, 'es'))
  const out = { ids, items }
  byClass.set(cls.id, out)
  return out
}

const SECTION_LABEL: Record<string, string> = {
  espadas: 'Espadas',
  hachas: 'Hachas',
  mazas: 'Mazas',
  lanzas: 'Lanzas',
  arcos: 'Arcos y ballestas',
  baculos: 'Báculos',
  escudos: 'Escudos',
  cascos: 'Cascos',
  armaduras: 'Armaduras',
  pantalones: 'Pantalones',
  guantes: 'Guantes',
  botas: 'Botas',
  'alas-y-orbes': 'Alas y orbes',
  accesorios: 'Accesorios',
  'joyas-y-consumibles': 'Joyas y consumibles',
  pergaminos: 'Pergaminos y libros',
}

export const sectionLabel = (section: number) => SECTION_LABEL[ITEM_SECTION_SLUG[section] ?? ''] ?? 'Otros'

/** Requisitos documentados del propio ítem (01_items: required_level_config, required_energy_config). */
export function itemRequirementText(item: Item): string {
  const parts: string[] = []
  if (item.requiredLevel) parts.push(`Nivel ${item.requiredLevel}`)
  if (item.requiredEnergy) parts.push(`Energía ${item.requiredEnergy}`)
  return parts.length ? parts.join(' · ') : 'Sin requisitos de nivel ni energía documentados'
}
